// ⚠️ MOCK DATA — Platform Admin console (PRD §5). Not wired to Firestore.
// Route-gated /padmin area for Tagnologys staff.

export interface AdminNavItem {
  key: string;
  icon: string;
}

// Platform Admin sections (PRD §5). Tenants / Plans / Tickets are built out;
// the rest render a short placeholder.
export const PADMIN_NAV: AdminNavItem[] = [
  { key: 'overview', icon: '📊' },
  { key: 'tenants', icon: '🏢' },
  { key: 'users', icon: '👤' },
  { key: 'plans', icon: '💳' },
  { key: 'billing', icon: '🧾' },
  { key: 'content', icon: '📄' },
  { key: 'health', icon: '❤️' },
  { key: 'templates', icon: '🧩' },
  { key: 'broadcasts', icon: '📣' },
  { key: 'integrations', icon: '🔌' },
  { key: 'abuse', icon: '🛡' },
  { key: 'compliance', icon: '⚖️' },
  { key: 'tickets', icon: '🎫' },
  { key: 'audit', icon: '📜' },
  { key: 'security', icon: '🔐' },
];

/**
 * The same sections, grouped for the sidebar. Fifteen items in one flat
 * scrolling tab strip is unreadable — grouping gives the operator a mental map
 * of the console (who my customers are / money / keeping WhatsApp healthy /
 * safety / support / the platform itself).
 */
export interface AdminNavGroup {
  /** i18n key for the group heading; null renders the item ungrouped at the top. */
  labelKey: string | null;
  items: AdminNavItem[];
}

export const PADMIN_GROUPS: AdminNavGroup[] = [
  { labelKey: null, items: [{ key: 'overview', icon: '📊' }] },
  {
    labelKey: 'pa.group.customers',
    items: [{ key: 'tenants', icon: '🏢' }, { key: 'users', icon: '👤' }],
  },
  {
    labelKey: 'pa.group.revenue',
    items: [{ key: 'plans', icon: '💳' }, { key: 'billing', icon: '🧾' }],
  },
  {
    labelKey: 'pa.group.ops',
    items: [
      { key: 'health', icon: '❤️' },
      { key: 'templates', icon: '🧩' },
      { key: 'broadcasts', icon: '📣' },
      { key: 'integrations', icon: '🔌' },
    ],
  },
  {
    labelKey: 'pa.group.safety',
    items: [{ key: 'abuse', icon: '🛡' }, { key: 'compliance', icon: '⚖️' }],
  },
  { labelKey: 'pa.group.support', items: [{ key: 'tickets', icon: '🎫' }] },
  {
    labelKey: 'pa.group.platform',
    items: [
      { key: 'content', icon: '📄' },
      { key: 'audit', icon: '📜' },
      { key: 'security', icon: '🔐' },
    ],
  },
];

export type TenantStatus = 'active' | 'trial' | 'past_due' | 'suspended';

export interface AdminTenant {
  id: number;
  name: string;
  plan: string;
  status: TenantStatus;
  subsUsed: number;
  subsLimit: number;
  msgsUsed: number;
  msgsLimit: number;
  createdAt: string;
}

export const ADMIN_TENANTS: AdminTenant[] = [
  { id: 1, name: 'Top Beauty', plan: 'Yearly', status: 'active', subsUsed: 4512, subsLimit: 5000, msgsUsed: 3127, msgsLimit: 10000, createdAt: '02 Jan 26' },
  { id: 2, name: 'Nizwa Herbs', plan: 'Monthly', status: 'active', subsUsed: 1840, subsLimit: 5000, msgsUsed: 980, msgsLimit: 10000, createdAt: '18 Feb 26' },
  { id: 3, name: 'Muscat Mart', plan: 'Trial', status: 'trial', subsUsed: 64, subsLimit: 100, msgsUsed: 210, msgsLimit: 500, createdAt: '20 Jun 26' },
  { id: 4, name: 'Sohar Sweets', plan: 'Monthly', status: 'past_due', subsUsed: 3001, subsLimit: 5000, msgsUsed: 8800, msgsLimit: 10000, createdAt: '11 Dec 25' },
  { id: 5, name: 'Salalah Style', plan: 'Monthly', status: 'suspended', subsUsed: 0, subsLimit: 5000, msgsUsed: 0, msgsLimit: 10000, createdAt: '03 Nov 25' },
];

export interface AdminPlan {
  id: number;
  name: string;
  priceMonthly: number;
  priceYearly: number;
  subscribers: number;
  messages: number;
  seats: number;
}

export const ADMIN_PLANS: AdminPlan[] = [
  { id: 1, name: 'Pro', priceMonthly: 14, priceYearly: 96, subscribers: 5000, messages: 10000, seats: 5 },
  { id: 2, name: 'Free Trial', priceMonthly: 0, priceYearly: 0, subscribers: 100, messages: 500, seats: 1 },
];

export type TicketStatus = 'open' | 'answered' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high';

export interface AdminTicket {
  id: string;
  tenant: string;
  subject: string;
  module: string;
  priority: TicketPriority;
  status: TicketStatus;
  updatedAt: string;
}

export const ADMIN_TICKETS: AdminTicket[] = [
  { id: 'TK-318', tenant: 'Top Beauty', subject: 'Template stuck in pending', module: 'Templates', priority: 'high', status: 'open', updatedAt: '2h ago' },
  { id: 'TK-317', tenant: 'Muscat Mart', subject: 'How do I import a CSV?', module: 'Subscribers', priority: 'normal', status: 'answered', updatedAt: '5h ago' },
  { id: 'TK-315', tenant: 'Nizwa Herbs', subject: 'Billing receipt request', module: 'Billing', priority: 'low', status: 'closed', updatedAt: '1d ago' },
];

export const ADMIN_STATS = [
  { key: 'tenants', value: '5' },
  { key: 'active', value: '2' },
  { key: 'mrr', value: '$70' },
  { key: 'tickets', value: '1' },
];
